/**
 * MFUMO WA TAFSIRI KALI (i18n) KWA AEGIS
 * Usaidizi kamili wa lugha nyingi na kupakia kwa kuendelea,
 * usaidizi wa RTL, na muonfigu mahsusi wa eneo.
 */

import { useState, useEffect, useCallback, ReactNode } from 'react';

import {
  SUPPORTED_LOCALES,
  type SupportedLocale,
  type TranslationKey,
  type TranslationValues,
  type TranslationDictionary,
  loadTranslations,
  getNestedValue,
  interpolate,
  detectBrowserLocale,
  getStoredLocale,
  storeLocale,
} from './utils';

import { I18nContext, type I18nContextType } from './context';

// Aina mahsusa kwa chaguo za ListFormat (Feature ya ES2021+ Intl)
interface ListFormatOptions {
  style?: 'long' | 'short' | 'narrow';
  type?: 'conjunction' | 'disjunction' | 'unit';
  localeMatcher?: 'lookup' | 'best fit';
}

// I18nContext and `I18nContextType` are provided by ./context.ts

// I18n Provider component
interface I18nProviderProps {
  children: ReactNode;
  defaultLocale?: SupportedLocale;
}

export function I18nProvider({ children, defaultLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(
    () => getStoredLocale() || defaultLocale || detectBrowserLocale()
  );
  const [translations, setTranslations] = useState<TranslationDictionary>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load translations when locale changes
  useEffect(() => {
    let mounted = true;

    async function load() {
      setIsLoading(true);
      const loaded = await loadTranslations(locale);
      if (mounted) {
        setTranslations(loaded);
        setIsLoading(false);

        // Update document attributes
        document.documentElement.lang = locale;
        document.documentElement.dir = SUPPORTED_LOCALES[locale].dir;
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [locale]);

  // Set locale function
  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    if (newLocale === locale) return;

    storeLocale(newLocale);
    setLocaleState(newLocale);
  }, [locale]);

  // Translation function
  const t = useCallback((key: TranslationKey, values?: TranslationValues): string => {
    const translation = getNestedValue(translations, key);

    if (!translation) {
      console.warn(`Missing translation for key: ${key}`);
      return key;
    }

    if (values) {
      return interpolate(translation, values);
    }

    return translation;
  }, [translations]);

  // Format number
  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions): string => {
    return new Intl.NumberFormat(locale, options).format(value);
  }, [locale]);

  // Format currency
  const formatCurrency = useCallback((value: number, currency = 'USD'): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(value);
  }, [locale]);

  // Format date
  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, options || {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
  }, [locale]);

  // Format relative time
  const formatRelativeTime = useCallback((date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (days > 0) return rtf.format(-days, 'day');
    if (hours > 0) return rtf.format(-hours, 'hour');
    if (minutes > 0) return rtf.format(-minutes, 'minute');
    return rtf.format(-seconds, 'second');
  }, [locale]);

  // Format list
  const formatList = useCallback((items: string[], options?: ListFormatOptions): string => {
    // Use Intl.ListFormat if available, otherwise fallback to join
    if (typeof Intl !== 'undefined' && 'ListFormat' in Intl) {
      return new (Intl as unknown as { ListFormat: new (locale: string, opts?: ListFormatOptions) => { format: (items: string[]) => string } }).ListFormat(locale, options || { style: 'long', type: 'conjunction' }).format(items);
    }
    return items.join(', ');
  }, [locale]);

  // RTL helpers
  const isRTL = SUPPORTED_LOCALES[locale].dir === 'rtl';
  const direction = SUPPORTED_LOCALES[locale].dir;

  const value: I18nContextType = {
    locale,
    setLocale,
    t,
    formatNumber,
    formatCurrency,
    formatDate,
    formatRelativeTime,
    formatList,
    isRTL,
    direction,
    isLoading,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}
// NOTE: utility helpers and types are exported from their dedicated modules
